import classNames from 'classnames';
import {formatMoney} from 'lib/formatters';
import {Counter} from 'modules/ProductsGrid/components/ProductGridItem/Counter/Counter';
import {ProductGridItemData} from 'modules/ProductsGrid/lib/types';
import {FC} from 'react';

import {Button, ButtonSize, ButtonType} from 'components/Button/Button';
import {Icon} from 'components/Icon/Icon';
import {Text} from 'components/Text/Text';

import styles from './ProductGridItem.module.css';

type Props = {
    className?: string;
    data: ProductGridItemData;
    removed?: boolean;
    amount: number;
    onChangeCount: (count: number) => void;
    onRemove: () => void;
    onRestore: () => void;
    onPreviewClick?: () => void;
    withoutRemovedState?: boolean;
};

export const ProductGridItem: FC<Props> = ({
    className,
    data,
    removed,
    amount,
    onChangeCount,
    onRemove,
    onRestore,
    onPreviewClick,
    withoutRemovedState = false,
}) => {
    const priceIsChanged = data.price.old !== data.price.current;

    const readonly = false;

    return (
        <div
            className={classNames(styles.block, className, {
                [styles.removed]: withoutRemovedState ? false : removed,
            })}
        >
            <img
                onClick={onPreviewClick}
                src={data.preview}
                className={classNames(styles.preview, {
                    [styles.clickable]: Boolean(onPreviewClick),
                })}
                alt=""
            />
            <div className={styles.productInfo}>
                <a className={styles.productLink} href={data.link}>
                    <Text.Bold14>{data.title}</Text.Bold14>
                </a>
                <Text.Regular12 className={styles.code}>
                    артикул: {data.article}
                </Text.Regular12>
            </div>

            <div
                className={classNames(styles.prices, {
                    [styles.withSale]: priceIsChanged,
                })}
            >
                {priceIsChanged && (
                    <div className={styles.oldPrice}>
                        <Text.Bold14 className={styles.oldPriceText}>
                            {formatMoney(data.price.old)}
                        </Text.Bold14>
                    </div>
                )}
                <Text.Bold16 className={styles.newPrice}>
                    {data.price.current === 0 ? (
                        <span className={styles.notAvailableLabel}>
                            Под заказ
                        </span>
                    ) : (
                        formatMoney(data.price.current)
                    )}
                </Text.Bold16>
            </div>

            <div className={styles.multiplicator}>x</div>

            <div
                className={classNames(styles.counterColumn, {
                    [styles.readonly]: readonly,
                })}
            >
                <Counter
                    className={styles.counter}
                    value={amount}
                    onChange={onChangeCount}
                    step={data.counter.addBy}
                    minValue={data.counter.min}
                    readonly={readonly}
                />
            </div>

            <div className={styles.equal}>=</div>

            <div
                className={classNames(styles.totalPrices, {
                    [styles.withSale]: priceIsChanged,
                })}
            >
                {priceIsChanged && (
                    <div className={styles.oldPrice}>
                        <Text.Bold14 className={styles.oldPriceText}>
                            {formatMoney(amount * data.price.old)}
                        </Text.Bold14>
                    </div>
                )}
                <Text.Bold16 className={styles.newPrice}>
                    {data.price.current === 0 ? (
                        <span className={styles.notAvailableLabel}>
                            Под заказ
                        </span>
                    ) : (
                        formatMoney(amount * data.price.current)
                    )}
                </Text.Bold16>
            </div>

            {withoutRemovedState && (
                <div className={styles.addButton}>
                    <Button
                        type={ButtonType.PrimaryOutline}
                        size={ButtonSize.XS}
                        onClick={onRestore}
                    >
                        {'Добавить в заказ'}
                    </Button>
                </div>
            )}

            {!withoutRemovedState && (
                <div className={styles.controls}>
                    <Icon
                        name="add"
                        className={classNames(styles.controlIcon, {
                            [styles.removed]: removed,
                        })}
                        onClick={removed ? onRestore : onRemove}
                    />
                </div>
            )}
        </div>
    );
};
